import { Dialog } from '@cimplico/components';
import { useRef, useState } from 'react';
import ReactCrop, { PixelCrop, type Crop, centerCrop, makeAspectCrop, PercentCrop } from 'react-image-crop';
import { useUploadUserAvatar } from '../../../queries/use-upload-user-avatar';
import { useUpdateUsers } from '../../../queries/use-update-users';
import { useUserContext } from '../../../providers/UserProvider';
import { toastNotification } from '../../../components/toast/Toast';

type AvatarImagePreviewProps = {
    imgSrc: string;
    onClose: () => void;
};

function AvatarImagePreview({ imgSrc, onClose }: AvatarImagePreviewProps): JSX.Element {
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const { id } = useUserContext();
    const imgRef = useRef<HTMLImageElement>(null);

    const uploadUserAvatarQuery = useUploadUserAvatar();
    const updateUserQuery = useUpdateUsers();

    function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number): PercentCrop {
        return centerCrop(makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight), mediaWidth, mediaHeight);
    }

    function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>): void {
        const { width, height } = e.currentTarget;
        setCrop(centerAspectCrop(width, height, 1));
    }

    async function getCanvasImage(image: HTMLImageElement, crop: PixelCrop): Promise<HTMLCanvasElement> {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            throw new Error('No 2d context');
        }

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const pixelRatio = window.devicePixelRatio;

        canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
        canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

        ctx.scale(pixelRatio, pixelRatio);
        ctx.imageSmoothingQuality = 'high';

        const cropX = crop.x * scaleX;
        const cropY = crop.y * scaleY;

        const centerX = image.naturalWidth / 2;
        const centerY = image.naturalHeight / 2;

        ctx.save();

        ctx.translate(-cropX, -cropY);
        ctx.translate(centerX, centerY);
        ctx.translate(-centerX, -centerY);
        ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, image.naturalWidth, image.naturalHeight);

        return canvas;
    }

    async function handleUpdateAvatar(): Promise<void> {
        if (!completedCrop) {
            throw new Error('Cropped image does not exist');
        }

        if (completedCrop?.width && completedCrop?.height && imgRef.current) {
            const canvas = await getCanvasImage(imgRef.current, completedCrop);

            canvas.toBlob((blob) => {
                if (!blob) {
                    throw new Error('Failed to create blob');
                }

                const file = new File([blob], 'avatar.png');

                uploadUserAvatarQuery.mutate(
                    { file },
                    {
                        onSuccess: (data) => {
                            updateUserQuery.mutate([{ id, fields: { avatarUrl: data.fileName } }], {
                                onSuccess: () => {
                                    toastNotification('success', 'Profile avatar successfully updated!');
                                    onClose();
                                },
                                onError: () => {
                                    toastNotification('error', 'Unable to update profile avatar!');
                                    onClose();
                                },
                            });
                        },
                    }
                );
            });
        }
    }

    return (
        <Dialog
            isOpen
            onClose={onClose}
            title="Image Crop"
            onPrimaryActionClick={handleUpdateAvatar}
            onSecondaryActionClick={onClose}
            primaryAction="Save"
            secondaryAction="Cancel"
            description="Crop your new profile picture. You can remove your profile picture at any time and it will be permanently deleted."
            type="info"
            isLoading={updateUserQuery.isLoading || uploadUserAvatarQuery.isLoading}
        >
            <div className="py-4 pr-[72px]">
                <ReactCrop
                    crop={crop}
                    onChange={(c): void => setCrop(c)}
                    onComplete={(c): void => setCompletedCrop(c)}
                    aspect={1}
                    circularCrop
                    maxHeight={150}
                    maxWidth={150}
                >
                    <img src={imgSrc} ref={imgRef} onLoad={onImageLoad} className="max-w-[300px] max-h-[300px]" />
                </ReactCrop>
            </div>
        </Dialog>
    );
}

export default AvatarImagePreview;
